import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import intl from "react-intl-universal";
import { TbSearch, TbX } from "react-icons/tb";
import {
  fetchBaasEntities,
  fetchBaasEntityById,
} from "../../../../../Services/baasEntidadesApi";

/**
 * Modal to search/select an existing PERSON (pers_…) from the BaaS instance.
 */
export const BaasExistingPersonPickerModal = ({
  open,
  instance,
  excludePersIds = [],
  onClose,
  onSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectingId, setSelectingId] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState([]);

  const excluded = useMemo(
    () =>
      new Set(
        (excludePersIds || [])
          .map((id) => String(id || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    [excludePersIds],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !instance) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setQuery("");

    fetchBaasEntities(instance, { entityType: "PERSON", pageSize: 100 })
      .then((items) => {
        if (cancelled) return;
        setPeople(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) {
          setPeople([]);
          setError(intl.get("baas.entidades.create.existingPersonLoadError"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, instance]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    return people.filter((person) => {
      const id = String(person?.id || "").trim();
      if (!id.toLowerCase().startsWith("pers_")) return false;
      if (excluded.has(id.toLowerCase())) return false;
      if (!q) return true;
      const haystack = [person.name, person.document, person.id]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [people, query, excluded]);

  const handleSelect = async (person) => {
    if (!instance || !person?.id || selectingId) return;
    setSelectingId(person.id);
    setError("");
    try {
      const detail = await fetchBaasEntityById(instance, person.id);
      onSelect?.(detail || person);
      onClose?.();
    } catch {
      // Fall back to list row when detail fetch fails.
      onSelect?.(person);
      onClose?.();
    } finally {
      setSelectingId("");
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={intl.get("baas.entidades.create.existingPersonTitle")}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-brand-dark-elevated"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.entidades.create.existingPersonTitle")}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.create.existingPersonHint")}
            </p>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-brand-dark dark:hover:bg-brand-dark-surface dark:hover:text-gray-100"
            aria-label={intl.get("common.cancel")}
            onClick={onClose}
          >
            <TbX className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
          <label className="relative block">
            <TbSearch
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={intl.get("baas.entidades.create.existingPersonSearch")}
              className="h-10 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-brand-dark outline-none focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark dark:text-gray-100"
              autoComplete="off"
            />
          </label>
        </div>

        <div className="min-h-[200px] flex-1 overflow-auto px-2 py-2">
          {loading ? (
            <p className="px-2 py-6 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.create.existingPersonLoading")}
            </p>
          ) : null}
          {!loading && error ? <p className="px-2 py-3 text-sm text-red-500">{error}</p> : null}
          {!loading && !error && filtered.length === 0 ? (
            <p className="px-2 py-6 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.create.existingPersonEmpty")}
            </p>
          ) : null}
          {!loading && filtered.length > 0 ? (
            <ul className="mb-0 divide-y divide-gray-100 dark:divide-brand-dark-border">
              {filtered.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left transition hover:bg-brand-gold/10 disabled:opacity-50"
                    disabled={Boolean(selectingId)}
                    onClick={() => handleSelect(person)}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">
                        {person.name || person.id}
                      </p>
                      <p className="mt-0.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                        {person.id}
                      </p>
                      {person.document ? (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {person.document}
                        </p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs font-medium text-[#b8860b]">
                      {selectingId === person.id
                        ? intl.get("baas.entidades.create.existingPersonSelecting")
                        : intl.get("baas.entidades.create.existingPersonSelect")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BaasExistingPersonPickerModal;
